import React, { useState, useMemo, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Button, Spinner } from 'react-bootstrap';
import { gamesToOptions } from '../hooks/useGames';
import api from '../api/api';

const SEARCH_DEBOUNCE_MS = 300;

export default function GamesSelect({
    games = [],
    value = '',
    onChange,
    placeholder = 'Spiel auswählen oder eintippen...',
    className,
    darkMode = false,
    cardBgClass = '',
    allowCreate = true,
    onCreate = async () => { },
}) {
    const [inputValue, setInputValue] = useState('');
    const [twitchResults, setTwitchResults] = useState([]); // [{id, name, box_art_url}]
    const [searching, setSearching] = useState(false);
    const debounceRef = useRef(null);
    const requestSeq = useRef(0);

    const localOptions = useMemo(() => gamesToOptions(games), [games]);

    // Normalisierung zentral
    const norm = (s) => (s ?? '').normalize('NFKC').trim().toLowerCase();

    const inputNorm = norm(inputValue);

    // Interaktive Twitch-Kategoriesuche (Helix "Search Categories") im Hintergrund,
    // damit man das Spiel nicht mehr nur aus der lokalen Liste auswählen oder frei
    // eintippen muss, sondern direkt von Twitch vorgeschlagene Treffer bekommt.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (inputNorm.length < 2) {
            setTwitchResults([]);
            setSearching(false);
            return;
        }

        const seq = ++requestSeq.current;
        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await api.get('/games/search', { params: { q: inputValue.trim() } });
                if (seq === requestSeq.current) {
                    setTwitchResults(Array.isArray(res.data) ? res.data : []);
                }
            } catch (e) {
                console.error('Fehler bei der Twitch-Spielsuche:', e);
                if (seq === requestSeq.current) setTwitchResults([]);
            } finally {
                if (seq === requestSeq.current) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(debounceRef.current);
    }, [inputNorm]);

    // Lokale Spiele + Twitch-Treffer zusammenführen, Duplikate (nach Name) entfernen -
    // lokale Einträge haben Vorrang, damit bereits verwendete Spiele oben/eindeutig bleiben.
    const options = useMemo(() => {
        const seen = new Set(localOptions.map((o) => norm(o.value)));
        const twitchOptions = twitchResults
            .filter((g) => g.name && !seen.has(norm(g.name)))
            .map((g) => ({ label: g.name, value: g.name, boxArtUrl: g.box_art_url, fromTwitch: true }));
        return [...localOptions, ...twitchOptions];
    }, [localOptions, twitchResults]);

    // Existenz-Check memoisiert (abhängig von games + inputValue)
    const exists = useMemo(
        () => games.some((g) => norm(g) === inputNorm),
        [games, inputNorm]
    );

    // Button nur zeigen, wenn Erstellen erlaubt, etwas getippt wurde und es noch nicht existiert
    const showCreate = allowCreate && inputNorm.length > 0 && !exists;

    // Gemeinsame Aktion: Neues Spiel erstellen (oder auswählen, wenn vorhanden)
    const createOrSelect = async (rawName) => {
        const name = rawName.trim();
        if (!name) return;

        const isExisting = games.some((g) => norm(g) === norm(name));

        if (!isExisting) {
            const ok = await onCreate(name);   // Parent fügt zur Liste hinzu und gibt true/false zurück
            if (ok) onChange?.(name);
        } else {
            // existiert schon → einfach auswählen
            onChange?.(name);
        }

        // Eingabe danach zurücksetzen
        setInputValue('');
    };

    // react-select ignoriert Bootstraps bg-*/text-*-Klassen für seine internen Teile
    // (Control/Menu/Option werden inline gestyled) - ohne explizite styles-Overrides
    // bleibt der Text im Darkmode dunkel auf dunklem Grund und ist unlesbar.
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '0.375rem',
            ...(darkMode
                ? { backgroundColor: '#2b3035', borderColor: state.isFocused ? '#6c757d' : '#495057' }
                : { borderColor: '#ced4da' }),
        }),
        singleValue: (base) => ({ ...base, color: darkMode ? '#f8f9fa' : base.color }),
        input: (base) => ({ ...base, color: darkMode ? '#f8f9fa' : base.color }),
        placeholder: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        menu: (base) => ({ ...base, backgroundColor: darkMode ? '#2b3035' : base.backgroundColor }),
        option: (base, state) => ({
            ...base,
            ...(darkMode
                ? {
                    backgroundColor: state.isSelected ? '#495057' : state.isFocused ? '#3a4046' : '#2b3035',
                    color: '#f8f9fa',
                }
                : {}),
        }),
        noOptionsMessage: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        dropdownIndicator: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        clearIndicator: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        indicatorSeparator: (base) => ({ ...base, backgroundColor: darkMode ? '#495057' : base.backgroundColor }),
    };

    return (
        <div>
            <Select
                options={options}
                placeholder={placeholder}
                isClearable
                isSearchable
                blurInputOnSelect={false}
                menuPlacement="auto"
                className={darkMode ? cardBgClass || className : className}
                value={value ? { label: value, value } : null}
                noOptionsMessage={() => (searching ? 'Suche läuft...' : 'Keine Treffer')}
                formatOptionLabel={(opt) => (
                    <div className="d-flex align-items-center gap-2">
                        {opt.boxArtUrl && (
                            <img
                                src={opt.boxArtUrl}
                                alt=""
                                width={20}
                                height={28}
                                style={{ objectFit: 'cover', borderRadius: 2 }}
                            />
                        )}
                        <span>{opt.label}</span>
                        {opt.fromTwitch && (
                            <span className={darkMode ? undefined : 'text-muted'} style={{ fontSize: '0.75em', color: darkMode ? '#adb5bd' : undefined }}>(Twitch)</span>
                        )}
                    </div>
                )}
                onChange={(opt) => {
                    // Auswahl nach oben geben (bei Twitch-Treffern zusätzlich lokal persistieren) …
                    if (opt?.fromTwitch) {
                        createOrSelect(opt.value);
                    } else {
                        onChange?.(opt ? opt.value : '');
                    }
                    // … und Tippfeld leeren
                    setInputValue('');
                }}
                onInputChange={(txt, { action }) => {
                    if (action === 'input-change') {
                        setInputValue(txt);
                        // beim Tippen direkt nach oben reichen
                        onChange?.(txt);
                    }
                    // WICHTIG: Nicht bei blur/menu-close leeren, sonst verschwindet der Create-Button zu früh
                }}
                onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                        // verhindert, dass react-select Enter intern konsumiert
                        e.preventDefault();
                        e.stopPropagation();
                        await createOrSelect(inputValue);
                    }
                }}
                styles={selectStyles}
            />

            {searching && (
                <Spinner animation="border" size="sm" className="mt-2 me-2" role="status" />
            )}

            {showCreate && (
                <Button
                    variant="success"
                    size="sm"
                    className="mt-2"
                    onClick={async () => {
                        await createOrSelect(inputValue);
                    }}
                >
                    ➕ Neues Spiel hinzufügen
                </Button>
            )}
        </div>
    );
}